import { createClient, SupabaseClient } from "@supabase/supabase-js";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

export const supabase: SupabaseClient | null = isSupabaseConfigured
  ? createClient(supabaseUrl!, supabaseAnonKey!)
  : null;

export const BDAY_SESSION_ID = "shared";

export type JournalEntry = {
  text: string;
  images: string[];
};

export type BdayRow = {
  id: string;
  session_id: string;
  timeline: Record<string, boolean>;
  checklists: Record<string, Record<string, boolean>>;
  date_choice: string | null;
  journals: Record<string, JournalEntry | string>;
  shopping_items: Record<string, string[]>;
  shopping_removed: Record<string, string[]>;
  notes: string;
  updated_at: string;
};

export type BdayAppState = {
  timeline: Record<string, boolean>;
  checklists: Record<string, Record<string, boolean>>;
  dateChoice: string | null;
  journals: Record<string, JournalEntry>;
  shoppingItems: Record<string, string[]>;
  shoppingRemoved: Record<string, string[]>;
  notes: string;
};

export const EMPTY_BDAY_STATE: BdayAppState = {
  timeline: {},
  checklists: {},
  dateChoice: null,
  journals: {},
  shoppingItems: {},
  shoppingRemoved: {},
  notes: "",
};

export function normalizeJournalEntry(value: unknown): JournalEntry {
  if (typeof value === "string") return { text: value, images: [] };
  if (value && typeof value === "object") {
    const entry = value as Partial<JournalEntry>;
    return {
      text: typeof entry.text === "string" ? entry.text : "",
      images: Array.isArray(entry.images) ? entry.images.filter((i): i is string => typeof i === "string") : [],
    };
  }
  return { text: "", images: [] };
}

export function rowToState(row: BdayRow): BdayAppState {
  const journals: Record<string, JournalEntry> = {};
  for (const [day, value] of Object.entries(row.journals ?? {})) {
    journals[day] = normalizeJournalEntry(value);
  }

  return {
    timeline: row.timeline ?? {},
    checklists: row.checklists ?? {},
    dateChoice: row.date_choice ?? null,
    journals,
    shoppingItems: row.shopping_items ?? {},
    shoppingRemoved: row.shopping_removed ?? {},
    notes: row.notes ?? "",
  };
}

export function stateToRow(state: BdayAppState) {
  return {
    session_id: BDAY_SESSION_ID,
    timeline: state.timeline,
    checklists: state.checklists,
    date_choice: state.dateChoice,
    journals: state.journals,
    shopping_items: state.shoppingItems,
    shopping_removed: state.shoppingRemoved,
    notes: state.notes,
    updated_at: new Date().toISOString(),
  };
}
